import { startBackgroundGeneration, preloadBackgroundVariations, showLoginRequired, setLoadingState, generateBackgroundPreview } from './imageGeneration.js';
import { getCurrentLanguage, applyTranslations } from '../translations.js';
import { initOAuthManager } from '../utils/oauthManager.js';
import { storage } from '../utils/storage.js';
import { showWarning, showError } from '../utils/UnifiedNotificationSystem.js';
import { getBlockedNsfwPromptMessage } from '../utils/nsfwTags.js';
import { getBlockedUnrecognizedPromptMessage } from './danbooruTags.js';
import { fetchLocationsMap, locObjectToArray } from '../../core/utils/hangoutUtils.js';

async function fetchAndCacheBackgrounds() {
  try {
    return locObjectToArray(await fetchLocationsMap());
  } catch (error) {
    console.error('Error fetching background options:', error);
    return [];
  }
}

export async function initializeBackgroundDropdown() {
  const data = await fetchAndCacheBackgrounds();
  const backgroundSelect = document.getElementById('generate-bg-1');
  const timeOfDaySelect = document.getElementById('time-of-day-1');
  if (!backgroundSelect || !timeOfDaySelect) return;

  const currentLang = getCurrentLanguage();
  const currentSelection = backgroundSelect.value;

  backgroundSelect.innerHTML = '<option value="" disabled selected data-translate="steps.setting.select_background">Select a background</option>';
  data.forEach(bg => {
    const option = document.createElement('option');
    option.value = bg.id;
    option.textContent = bg.name;
    option.setAttribute('data-translate', `background_${bg.id}`);
    backgroundSelect.appendChild(option);
  });

  if (currentSelection && !currentSelection.startsWith('new-bg-')) {
    backgroundSelect.value = currentSelection;
  }

  timeOfDaySelect.innerHTML = `
    <option value="0.1" data-translate="steps.setting.day">Day</option>
    <option value="0.2" data-translate="steps.setting.sunset">Sunset</option>
    <option value="0.3" data-translate="steps.setting.night">Night</option>
  `;

  applyTranslations('create-scenario', currentLang);

  const customBgEls = () => [
    document.getElementById('custom-bg-name'),
    document.getElementById('custom-bg-textarea'),
    document.getElementById('custom-bg-generate-btn'),
  ];

  const setCustomBgVisible = (visible) => {
    customBgEls().forEach((el) => el?.classList.toggle('is-hidden', !visible));
  };

  const applyCachedBgPreview = (url) => {
    const prev = document.getElementById('bg-preview-1');
    if (!prev || !url) return;
    prev.querySelectorAll('img, video').forEach((n) => n.remove());
    prev.querySelector('.preview-placeholder')?.classList.add('is-hidden');
    prev.classList.add('has-bg-image');
    prev.style.backgroundImage = `url(${url})`;
  };

  const bindCustomBgControls = () => {
    const area = document.getElementById('custom-bg-textarea');
    const nameInput = document.getElementById('custom-bg-name');
    const genBtn = document.getElementById('custom-bg-generate-btn');
    if (!area || !nameInput || !genBtn) return;

    if (!nameInput.dataset.listenerAdded) {
      nameInput.dataset.listenerAdded = 'true';
      nameInput.addEventListener('input', () => {
        const bgSelect = document.getElementById('generate-bg-1');
        const selectedValue = bgSelect?.value;
        if (selectedValue && selectedValue.startsWith('new-bg-')) {
          const selectedOption = bgSelect.querySelector(`option[value="${selectedValue}"]`);
          if (selectedOption) {
            selectedOption.textContent = `${nameInput.value.trim() || 'Custom Background'} (New)`;
          }
        }
      });
    }

    if (!genBtn.dataset.listenerAdded) {
      genBtn.dataset.listenerAdded = 'true';
      genBtn.addEventListener('click', async () => {
        const oauthManager = initOAuthManager();
        if (!(await oauthManager.isAuthenticated())) {
          showLoginRequired();
          return;
        }

        const promptTxt = area.value.trim();
        const bgName = nameInput.value.trim();
        if (!promptTxt) {
          showWarning('Please enter a background description.', { icon: 'exclamation-triangle', duration: 4000 });
          return;
        }
        if (!bgName) {
          showWarning('Please enter a background name.', { icon: 'exclamation-triangle', duration: 4000 });
          return;
        }
        const nsfwBlock = getBlockedNsfwPromptMessage([bgName, promptTxt].join(', '));
        if (nsfwBlock) {
          showWarning(nsfwBlock, { icon: 'exclamation-triangle', duration: 0, closable: true });
          return;
        }
        const unrecognizedBlock = await getBlockedUnrecognizedPromptMessage(promptTxt);
        if (unrecognizedBlock) {
          showWarning(unrecognizedBlock, { icon: 'exclamation-triangle', duration: 0, closable: true });
          return;
        }

        try {
          setLoadingState('custom-bg-generate-btn', true);
          const syntheticId = 'new-bg-' + bgName.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 48);
          const timeOfDay = document.getElementById('time-of-day-1')?.value || '0.1';
          const bgSelect = document.getElementById('generate-bg-1');
          if (bgSelect) {
            Array.from(bgSelect.options)
              .filter((o) => o.value && o.value.startsWith('new-bg-'))
              .forEach((o) => o.remove());
            const opt = new Option(`${bgName} (New)`, syntheticId);
            bgSelect.insertBefore(opt, bgSelect.options[1] || null);
            bgSelect.value = syntheticId;
          }
          await generateBackgroundPreview(promptTxt, 'bg-preview-1', timeOfDay);
          const imgEl = document.querySelector('#bg-preview-1 img');
          if (imgEl?.src) {
            window.syntheticBackgroundPreviews = window.syntheticBackgroundPreviews || {};
            window.syntheticBackgroundPreviews[syntheticId] = imgEl.src;
          }
        } catch (error) {
          console.error('Error generating custom background:', error);
          showError('Failed to generate background: ' + error.message, { icon: 'exclamation-circle', duration: 5000 });
        } finally {
          setLoadingState('custom-bg-generate-btn', false);
        }
      });
    }
  };

  window.showCustomBackgroundUI = () => {
    setCustomBgVisible(true);
    bindCustomBgControls();
    const currentBgId = document.getElementById('generate-bg-1')?.value;
    if (!(currentBgId && currentBgId.startsWith('new-bg-'))) {
      const area = document.getElementById('custom-bg-textarea');
      const nameInput = document.getElementById('custom-bg-name');
      if (area) area.value = '';
      if (nameInput) nameInput.value = '';
    }
  };

  bindCustomBgControls();

  const updateBackgroundPreview = async () => {
    const backgroundId = backgroundSelect.value;
    const timeOfDay = timeOfDaySelect.value;
    const nameInput = document.getElementById('custom-bg-name');
    const isCustomBackground = backgroundId && backgroundId.startsWith('new-bg-');

    setCustomBgVisible(!!isCustomBackground);

    if (isCustomBackground && nameInput && !nameInput.value.trim()) {
      const selectedOption = backgroundSelect.selectedOptions?.[0];
      if (selectedOption?.textContent) {
        nameInput.value = selectedOption.textContent.replace(/\s*\(New\)$/, '').trim();
      }
    }

    if (isCustomBackground) {
      const area = document.getElementById('custom-bg-textarea');
      let basePrompt = '';
      if (area?.value?.trim()) {
        basePrompt = area.value.trim();
      } else if (window.syntheticBackgroundPrompts) {
        const anyKey = Object.keys(window.syntheticBackgroundPrompts).find((k) => k.startsWith(`${backgroundId}.`));
        if (anyKey) basePrompt = window.syntheticBackgroundPrompts[anyKey];
      }
      if (!basePrompt) basePrompt = 'detailed background';
      if (area && !area.value) area.value = basePrompt;

      window.syntheticBackgroundPreviews = window.syntheticBackgroundPreviews || {};
      const cacheKey = `${backgroundId}.${timeOfDay}`;
      const cachedUrl = window.syntheticBackgroundPreviews[cacheKey];
      if (cachedUrl) {
        applyCachedBgPreview(cachedUrl);
        return;
      }

      await generateBackgroundPreview(basePrompt, 'bg-preview-1', timeOfDay);
      const prevImg = document.querySelector('#bg-preview-1 img');
      if (prevImg?.src) {
        window.syntheticBackgroundPreviews[cacheKey] = prevImg.src;
        window.syntheticBackgroundPrompts = window.syntheticBackgroundPrompts || {};
        window.syntheticBackgroundPrompts[cacheKey] = basePrompt.trim();
        try {
          const draft = storage.getJSON('scenario_draft_v2') || {};
          draft.__backgroundPrompts = draft.__backgroundPrompts || {};
          draft.__backgroundPrompts[cacheKey] = window.syntheticBackgroundPrompts[cacheKey];
          storage.setJSON('scenario_draft_v2', draft);
        } catch (e) {
          console.warn('[backgroundDropdown] Failed to persist background prompt:', e);
        }
      }
      if (area) area.value = basePrompt.trim();
      return;
    }

    if (backgroundId && !backgroundId.startsWith('new-bg-')) {
      await preloadBackgroundVariations(backgroundId);
      startBackgroundGeneration(1, timeOfDay);
    }
  };

  backgroundSelect.addEventListener('change', updateBackgroundPreview);
  timeOfDaySelect.addEventListener('change', updateBackgroundPreview);

  updateBackgroundPreview();

  document.addEventListener('languageSwitched', () => {
    applyTranslations('create-scenario', getCurrentLanguage());
  });
}
